/**
 * skyAgent streaming chat hook (SkyCRM) — ported from skySchedule's
 * `useSkyAgentChat`, kept deliberately close to it (STATUS-skyagent.md D5).
 *
 * TRANSPORT (Contract B, frozen): the chat endpoint is Server-Sent Events
 * delivered over a normal POST, because it needs an `Authorization` header and
 * `EventSource` cannot send one. We stream the response body, split on the SSE
 * frame delimiter (\n\n), and parse each `data: {...}` payload until `[DONE]`.
 * `: heartbeat` comment lines are skipped by the `data:` prefix test.
 *
 * DIVERGENCES FROM skySchedule, both forced by the frozen contract:
 *   1. skySchedule handles `todo_update` / `user_input_required` / `cancelled`
 *      chunk types. Contract B's union has SEVEN members and none of those are
 *      in it, so they are not implemented here. An unrecognised type is ignored
 *      rather than guessed at.
 *   2. `chunk.toolCall.name` is never read. Tool activity renders the
 *      server-rendered humanLabel carried in `content` only — a raw tool name or
 *      an endpoint path must never reach the UI.
 *
 * The hook is transport-injectable (`opts.streamChat`) so the panel can be
 * driven by the mock SSE stream without a server, per the Track B brief.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getInstanceIdFromUrl } from "@/utils/instanceId";
import type { IRootReducerState } from "@/store/IRootReducer";
import type { RootState } from "@/store";
import type { ChatChunk, TurnOutcome } from "@/store/api/crmSkyAgentApi";
import { consumeSseStream } from "./sseStream";
import { useDisplayTz } from "@/hooks/useDisplayTz";
import { useCommunityTz } from "@/hooks/useCommunityTz";

export type ChatRole = "user" | "assistant";

export interface ChatMessage {
  id: string;
  role: ChatRole;
  content: string;
  /**
   * How the turn that produced this answer ended (assistant messages only, and
   * only for a turn streamed in THIS session — a thread reopened from the rail
   * carries none, because the outcome is not persisted with the text). Absent
   * means "finished normally, or the server did not say".
   */
  outcome?: TurnOutcome;
}

/** One step in the live activity strip. `label` is ALWAYS a server humanLabel. */
export interface ActivityStep {
  id: string;
  label: string;
  status: "running" | "done";
  /**
   * `thinking` is the placeholder shown before the first tool call; `tool` is a
   * real step. They are distinguished because they have different lifetimes: a
   * tool step is a record of what the agent did and stays, while "Thinking" is
   * pure progress and reads as a stuck spinner if it outlives the turn.
   */
  kind: "thinking" | "tool";
}

export interface StreamChatArgs {
  /** Chat/Cowork intent for THIS turn. Omitted = cowork, so the mock transport and
   *  any other caller that predates this keeps today's behaviour. */
  mode?: "chat" | "cowork";
  sessionId: string | null;
  message: string;
  context?: string;
  signal: AbortSignal;
  /** Called once per parsed chunk, in arrival order. */
  onChunk: (chunk: ChatChunk) => void;
}

export type StreamChatFn = (args: StreamChatArgs) => Promise<void>;

export interface UseSkyAgentChatOptions {
  /** Community scope echoed on every turn so follow-ups stay scoped. */
  context?: string;
  /** Override the transport (the mock SSE stream in tests / Track-A-less dev). */
  streamChat?: StreamChatFn;
  /** Session factory; defaults to the real POST /sessions. */
  createSession?: () => Promise<string>;
}

let idCounter = 0;
const nextId = () => `m_${++idCounter}`;

/** Reset the module-local id counter. Test-only — keeps ids deterministic. */
export function __resetSkyAgentIds() {
  idCounter = 0;
}

export function useSkyAgentChat(opts: UseSkyAgentChatOptions = {}) {
  const { context, streamChat, createSession } = opts;

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [activity, setActivity] = useState<ActivityStep[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);

  // Fresh session per mount. The id is intentionally NOT persisted across
  // reloads: the thread starts blank anyway, and a stale id surfaces as
  // "Session not found".
  const sessionIdRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const assistantMsgIdRef = useRef<string | null>(null);

  /**
   * Re-entrancy latch for `send`, deliberately a ref and NOT `isStreaming`.
   *
   * `isStreaming` is state: it is only visible to callers on the NEXT render.
   * Three clicks on Send inside one tick therefore all read `false` and all
   * proceed — which created three sessions and three concurrent turns, and in
   * Cowork mode would have proposed the same action three times.
   *
   * The latch also has to be taken BEFORE `await startSession()`, since the
   * duplicate-session window is that await, not the stream that follows it.
   */
  const sendingRef = useRef(false);

  const accessToken = useSelector((s: IRootReducerState) => s.authReducer?.accessToken);

  /**
   * The header's community selection (D9) — the DEFAULT scope for every answer.
   * `null` means "All communities".
   *
   * Read straight from the slice rather than through `useSelectedCommunity`,
   * which additionally fires an accessible-communities query and a
   * stale-selection reconcile effect. Opening a chat panel should not trigger
   * either; this hook only needs to know what is currently selected.
   *
   * Sent as an ID, never as a name: the server resolves the label itself, so the
   * model is told the scope by the server rather than by the client. A
   * client-supplied community NAME would be a string the model quotes verbatim
   * into an answer without anything having verified it.
   */
  // Typed via `RootState` (inferred from the store) rather than the hand-written
  // `IRootReducerState`, which predates `communitySlice` and does not list it.
  // Widening that interface would be editing shared app types to suit one panel;
  // the inferred type already knows the slice exists.
  const selectedCommunityId = useSelector(
    (s: RootState) => s.communityReducer?.selectedCommunityId ?? null,
  );
  /**
   * The zone THIS SCREEN renders in — community zone in community mode, personal
   * otherwise. Paired exactly as `DayPlannerTab` pairs them, because the agent's
   * dates have to agree with the dates rendered beside them.
   */
  const viewerTz = useDisplayTz(useCommunityTz());

  const appendToAssistant = useCallback((text: string) => {
    const id = assistantMsgIdRef.current;
    if (!id) return;
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, content: m.content + text } : m)));
  }, []);

  /** Stamp the structured ending on the answer it belongs to. */
  const setOutcomeOnAssistant = useCallback((outcome: TurnOutcome) => {
    const id = assistantMsgIdRef.current;
    if (!id) return;
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, outcome } : m)));
  }, []);

  const handleChunk = useCallback(
    (chunk: ChatChunk) => {
      // Whatever the chunk's type, an `outcome` riding on it belongs to this
      // turn's answer. Read BEFORE the switch so the terminal `error` chunk —
      // which never appends text — still lands its ending on the bubble.
      if (chunk.outcome) setOutcomeOnAssistant(chunk.outcome);

      switch (chunk.type) {
        case "text":
        case "result":
          appendToAssistant(chunk.content);
          break;
        case "thinking":
        case "tool_start":
          // `content` is the server's humanLabel. `chunk.toolCall` is
          // deliberately not read — see the header note.
          setActivity((prev) => [
            ...prev.map((s) => ({ ...s, status: "done" as const })),
            {
              id: nextId(),
              label: chunk.content,
              status: "running",
              kind: chunk.type === "thinking" ? "thinking" : "tool",
            },
          ]);
          break;
        case "tool_progress":
          setActivity((prev) =>
            prev.map((s, i) => (i === prev.length - 1 ? { ...s, label: chunk.content } : s)),
          );
          break;
        case "tool_complete":
          // Mark every step done, and let a non-empty completion label REPLACE the
          // in-progress one on the step that just finished. "Found 12 open
          // prospects" tells the user what happened; leaving "Looking up open
          // prospects" on screen after it finished tells them nothing and reads
          // as a stuck spinner.
          //
          // skySchedule sends a bare "Done" here and drops it on the floor. We
          // send a real label instead, so it is worth rendering — but the guard
          // stays permissive rather than empty-checking only, because a blank or
          // whitespace label would silently erase a good one.
          setActivity((prev) => {
            const label = chunk.content?.trim();
            const lastIdx = prev.length - 1;
            return prev.map((s, i) => ({
              ...s,
              label: label && i === lastIdx ? label : s.label,
              status: "done" as const,
            }));
          });
          break;
        case "error":
          // A stale/expired session cannot be reused — drop it so the next send
          // transparently creates a fresh one.
          if (/session not found/i.test(chunk.content || "")) {
            sessionIdRef.current = null;
          }
          setError(chunk.content || "Something went wrong.");
          break;
        default:
          // Unknown type: ignore. Contract B's union is closed; guessing at an
          // unlisted type is how a UI starts rendering things it shouldn't.
          break;
      }
    },
    [appendToAssistant],
  );

  /** Real transport: SSE-over-POST against Contract B's chat route. */
  const defaultStreamChat = useCallback<StreamChatFn>(
    async ({ sessionId, message, context: ctx, mode, signal, onChunk }) => {
      const instanceId = getInstanceIdFromUrl();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

      const res = await fetch(`/api/v1/crm/skyagent/chat?instanceId=${instanceId ?? ""}`, {
        method: "POST",
        headers,
        credentials: "include",
        // communityId is what actually scopes the turn (D9). `context` stays on
        // the wire as the drawer's free-text hint ("about this prospect"); the
        // server does not feed it to the model today.
        body: JSON.stringify({
          sessionId,
          message,
          context: ctx,
          communityId: selectedCommunityId,
          // Per TURN, not per session: the server rebuilds the tool catalog on
          // every request, so the switch takes effect on the very next message
          // rather than needing a new thread.
          mode,
          // The zone THIS SCREEN is rendering in, sent so the agent's "today" and
          // its printed times cannot disagree with the dates beside them. The
          // server treats it as untrusted and validates before use, falling back to
          // the account resolver — the same contract the Day Planner and the
          // Standup Board already use with `?tz=`. It carries the COMMUNITY zone in
          // community mode, which the account resolver alone does not know about.
          tz: viewerTz,
        }),
        signal,
      });

      if (!res.ok || !res.body) {
        throw new Error(`Chat request failed (${res.status})`);
      }
      await consumeSseStream(res.body, onChunk);
    },
    [accessToken, selectedCommunityId, viewerTz],
  );

  const defaultCreateSession = useCallback(async (): Promise<string> => {
    const instanceId = getInstanceIdFromUrl();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    const res = await fetch(`/api/v1/crm/skyagent/sessions?instanceId=${instanceId ?? ""}`, {
      method: "POST",
      headers,
      credentials: "include",
      // Stamped on the thread so a later reader knows which community it opened in.
      body: JSON.stringify({ communityId: selectedCommunityId }),
    });
    if (!res.ok) throw new Error(`Could not start a conversation (${res.status})`);
    const body = (await res.json()) as { sessionId?: string; enabled?: boolean };
    if (body.enabled === false) throw new Error("skyAgent is turned off for this tenant.");
    if (!body.sessionId) throw new Error("Could not start a conversation.");
    return body.sessionId;
  }, [accessToken, selectedCommunityId]);

  const transport = streamChat ?? defaultStreamChat;
  const startSession = createSession ?? defaultCreateSession;

  const send = useCallback(
    async (message: string, mode?: "chat" | "cowork") => {
      const trimmed = message.trim();
      if (!trimmed || sendingRef.current) return;
      sendingRef.current = true;

      setError(null);
      setActivity([]);

      // Optimistically render the user's turn + an empty assistant turn.
      const userMsg: ChatMessage = {
        id: nextId(),
        role: "user",
        content: trimmed,
      };
      const assistantMsg: ChatMessage = {
        id: nextId(),
        role: "assistant",
        content: "",
      };
      assistantMsgIdRef.current = assistantMsg.id;
      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setIsStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        if (!sessionIdRef.current) {
          const sid = await startSession();
          // `reset()` (New task, or a community switch) aborts the controller,
          // but it cannot cancel the session POST that is already in flight.
          // Committing the id here anyway re-attached the just-cleared thread to
          // a session belonging to the PREVIOUS community scope, so the next
          // message landed in a conversation the user thought they had left.
          if (controller.signal.aborted) return;
          sessionIdRef.current = sid;
          setCurrentSessionId(sid);
        }

        await transport({
          sessionId: sessionIdRef.current,
          message: trimmed,
          // Community scope is sent on EVERY turn as a separate field, never
          // baked into the message, so a follow-up ("and the tours?") stays
          // scoped while the stored history/title stays clean.
          context,
          mode,
          signal: controller.signal,
          onChunk: handleChunk,
        });
      } catch (err) {
        if ((err as Error)?.name !== "AbortError") {
          setError(err instanceof Error ? err.message : "Chat failed");
        }
      } finally {
        setIsStreaming(false);
        // CLEAR the strip. It is progress, not a record.
        //
        // Two earlier attempts kept some of it around: first everything, then
        // just the tool steps. Both leave a column of ticked checkmarks sitting
        // UNDER the finished answer, describing work that is over — the answer
        // is the output, and a log of how it was produced is noise once it
        // exists. Anything worth keeping belongs in the answer itself.
        setActivity([]);
        abortRef.current = null;
        // Released on EVERY exit — success, network failure, and abort alike.
        // A latch that leaks on the error path would wedge the composer shut
        // for the rest of the session.
        sendingRef.current = false;
      }
    },
    [context, handleChunk, startSession, transport],
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  /**
   * Abort the in-flight turn when the hook goes away.
   *
   * Without this, closing the drawer or navigating away mid-answer left the
   * server generating the rest of the turn — real Gemini tokens for an answer
   * nobody would ever see — while the chunk handlers kept calling setMessages on
   * an unmounted hook.
   *
   * Empty dep array on purpose: this must run on unmount only. `abortRef` is a
   * ref, so the cleanup always sees the current controller without re-subscribing.
   *
   * The server side of this is `crmAgentRunner`'s try/finally — the abort now
   * happens on every drawer close rather than rarely, and without that finally it
   * would silently drop the turn's thread row and audit row each time.
   */
  useEffect(() => () => abortRef.current?.abort(), []);

  const reset = useCallback(() => {
    stop();
    sessionIdRef.current = null;
    setCurrentSessionId(null);
    setMessages([]);
    setActivity([]);
    setError(null);
  }, [stop]);

  /** Open a past conversation from the history rail. */
  const loadSession = useCallback(
    (sessionId: string, prior: Array<{ role: ChatRole; content: string }>) => {
      stop();
      sessionIdRef.current = sessionId;
      setCurrentSessionId(sessionId);
      setMessages(prior.map((m) => ({ id: nextId(), role: m.role, content: m.content })));
      setActivity([]);
      setError(null);
    },
    [stop],
  );

  return useMemo(
    () => ({
      messages,
      activity,
      isStreaming,
      error,
      currentSessionId,
      send,
      stop,
      reset,
      loadSession,
    }),
    [messages, activity, isStreaming, error, currentSessionId, send, stop, reset, loadSession],
  );
}
